import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ShieldAlert, ShieldCheck } from "lucide-react";

interface Props { range: 'P' | 'R' }

interface TaaCap { scope: string; cap_pct: number; description: string | null }
interface FundWeight { fund_id: string; portfolio: number; weight_pct: number; effective_date: string }
interface FundLite { id: string; fe_fund_name: string; fund_name: string; is_current: boolean; fund_manager: string | null; fund_class: string | null }

const PORTFOLIOS = [1, 2, 3, 4, 5];

export default function TAACapsPanel({ range }: Props) {
  const weightsTable = range === 'P' ? 'ba_cbam_fund_weights' : 'ba_r_cbam_fund_weights';

  const { data: caps = [] } = useQuery({
    queryKey: ['policy-taa-caps'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('policy_taa_caps' as any)
        .select('scope, cap_pct, description')
        .order('scope');
      if (error) throw error;
      return (data ?? []) as unknown as TaaCap[];
    },
  });

  const { data: weights = [] } = useQuery({
    queryKey: [`taa-fund-weights`, range],
    queryFn: async () => {
      const { data, error } = await supabase
        .from(weightsTable as any)
        .select('fund_id, portfolio, weight_pct, effective_date');
      if (error) throw error;
      const latest = new Map<string, FundWeight>();
      for (const w of (data as any) as FundWeight[]) {
        const key = `${w.fund_id}-${w.portfolio}`;
        const prev = latest.get(key);
        if (!prev || w.effective_date > prev.effective_date) latest.set(key, w);
      }
      return Array.from(latest.values());
    },
  });

  const { data: funds = [] } = useQuery({
    queryKey: ['taa-funds-lite'],
    queryFn: async () => {
      const { data, error } = await supabase.from('ba_cbam_funds').select('id, fe_fund_name, fund_name, is_current, fund_manager, fund_class');
      if (error) throw error;
      return data as FundLite[];
    },
  });

  const fundById = useMemo(() => {
    const m = new Map<string, FundLite>();
    for (const f of funds) m.set(f.id, f);
    return m;
  }, [funds]);
  const fundNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of funds) m.set(f.id, f.fe_fund_name || f.fund_name);
    return m;
  }, [funds]);

  const singleFundCap = caps.find(c => c.scope === 'single_fund_max')?.cap_pct ?? null;

  // Top single-fund weight per portfolio + breach flag
  const topByLevel = useMemo(() => {
    const m = new Map<number, { fund_id: string; weight: number }[]>();
    for (const p of PORTFOLIOS) {
      const rows = weights
        .filter(w => w.portfolio === p && Number(w.weight_pct) > 0)
        .map(w => ({ fund_id: w.fund_id, weight: Number(w.weight_pct) }))
        .sort((a, b) => b.weight - a.weight)
        .slice(0, 5);
      m.set(p, rows);
    }
    return m;
  }, [weights]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>TAA Caps (Tier 1 §8)</CardTitle>
          <CardDescription>Tactical-allocation guardrails. Edit in Compliance → Thresholds.</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Cap</TableHead>
                <TableHead className="text-right">Limit (%)</TableHead>
                <TableHead>Description</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {caps.map(c => (
                <TableRow key={c.scope}>
                  <TableCell className="font-medium text-sm capitalize">{c.scope.replace(/_/g, ' ')}</TableCell>
                  <TableCell className="text-right tabular-nums">{Number(c.cap_pct).toFixed(1)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{c.description ?? '—'}</TableCell>
                </TableRow>
              ))}
              {caps.length === 0 && (
                <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground text-sm py-6">No caps configured.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Top 5 Holdings vs §8 Single-Fund Cap — {range}</CardTitle>
          <CardDescription>
            Largest single-fund weights per portfolio. Cap: <span className="font-medium text-foreground">{singleFundCap != null ? `${singleFundCap}%` : 'not set'}</span>.
          </CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Portfolio</TableHead>
                <TableHead>Rank</TableHead>
                <TableHead>Fund</TableHead>
                <TableHead>Fund House</TableHead>
                <TableHead>Fund Class</TableHead>
                <TableHead className="text-right">Weight (%)</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {PORTFOLIOS.flatMap(p => {
                const rows = topByLevel.get(p) ?? [];
                if (rows.length === 0) {
                  return [(
                    <TableRow key={`${p}-empty`}>
                      <TableCell className="font-medium">{range}{p}</TableCell>
                      <TableCell colSpan={6} className="text-muted-foreground text-sm">No holdings</TableCell>
                    </TableRow>
                  )];
                }
                return rows.map((r, idx) => {
                  const breach = singleFundCap != null && r.weight > singleFundCap;
                  const amber = singleFundCap != null && !breach && r.weight >= singleFundCap * 0.95;
                  const f = fundById.get(r.fund_id);
                  return (
                    <TableRow key={`${p}-${r.fund_id}`}>
                      <TableCell className="font-medium">{idx === 0 ? `${range}${p}` : ''}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">#{idx + 1}</TableCell>
                      <TableCell className="text-sm">{fundNameById.get(r.fund_id) ?? r.fund_id}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{f?.fund_manager ?? '—'}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{f?.fund_class ?? '—'}</TableCell>
                      <TableCell className={`text-right tabular-nums ${breach ? 'text-destructive font-semibold' : amber ? 'text-amber-600 font-medium' : ''}`}>
                        {r.weight.toFixed(2)}
                      </TableCell>
                      <TableCell>
                        {breach
                          ? <Badge variant="destructive" className="gap-1"><ShieldAlert className="h-3 w-3" />Over cap</Badge>
                          : amber
                          ? <Badge className="gap-1 bg-amber-500 text-white hover:bg-amber-600"><ShieldAlert className="h-3 w-3" />Near cap</Badge>
                          : <Badge variant="secondary" className="gap-1"><ShieldCheck className="h-3 w-3" />Within</Badge>}
                      </TableCell>
                    </TableRow>
                  );
                });
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
